import React, { useEffect, useMemo, useRef, useState } from "react";
import { Cat, FilterChip, PageHeader, Panel, StatePanel, useSort } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { fmtNumber, programStyle, readRecentCenterIds } from "./tenantModel";

const PROGRAM_FILTERS = [
  { key: "firefighter", label: "Firefighter" },
  { key: "emt", label: "EMS" },
  { key: "general", label: "General" },
];

const programKey = (program) => (program === "firefighter" || program === "emt" ? program : "general");

/**
 * The tenant switcher: find a training center by name, city or programme and open it.
 * Recent centers come first; pressing / jumps to the search box.
 */
export default function TenantList({ tenants, error, query, onQuery, onOpen }) {
  const [program, setProgram] = useState("all");
  const searchRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const counts = useMemo(() => {
    const out = { firefighter: 0, emt: 0, general: 0 };
    tenants.forEach((t) => { out[programKey(t.program)] += 1; });
    return out;
  }, [tenants]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenants.filter((t) => {
      if (program !== "all" && programKey(t.program) !== program) return false;
      return !q || `${t.name} ${t.code} ${t.region}`.toLowerCase().includes(q);
    });
  }, [tenants, program, query]);

  const [rows, th] = useSort(filtered, ["name", "asc"]);

  const recent = useMemo(() => {
    const byId = new Map(tenants.map((t) => [String(t.id), t]));
    return readRecentCenterIds().map((id) => byId.get(id)).filter(Boolean);
  }, [tenants]);

  return (
    <>
      <PageHeader
        title="Tenant switcher"
        desc="Find a training center to open its workspace or impersonate a user."
      />

      {recent.length > 0 && !query && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <span className="eyebrow" style={{ marginRight: 4 }}>Recent</span>
          {recent.map((t) => <FilterChip key={t.id} icon={<I.clock s={13} />} label={t.name} onClick={() => onOpen(t)} />)}
        </div>
      )}

      <Panel>
        <div className="panel-h">
          <label className="srch">
            <I.search />
            <input ref={searchRef} placeholder="Search name, code or city" value={query} onChange={(e) => onQuery(e.target.value)} aria-label="Search training centers" />
            <span className="kbd" aria-hidden="true">/</span>
          </label>
          <div className="tb">
            <FilterChip label="All" on={program === "all"} onClick={() => setProgram("all")} />
            {PROGRAM_FILTERS.map((p) => (
              <FilterChip key={p.key} label={p.label} count={counts[p.key]} on={program === p.key} onClick={() => setProgram(p.key)} />
            ))}
          </div>
        </div>

        {error ? (
          <StatePanel kind="error" what="training centers" error={error} />
        ) : filtered.length === 0 ? (
          tenants.length === 0 ? (
            <StatePanel
              kind="empty"
              what="training centers"
              why="No training centers exist yet. Create one with Tenant Setup."
            />
          ) : (
            <StatePanel
              kind="empty"
              what="matching training centers"
              why={`Nothing matches “${query || PROGRAM_FILTERS.find((p) => p.key === program)?.label || ""}”. Clear the search or filters to see all ${tenants.length}.`}
              action={<button type="button" className="b sec" onClick={() => { onQuery(""); setProgram("all"); }}>Clear filters</button>}
            />
          )
        ) : (
          <div className="tw">
            <table className="tbl">
              <thead>
                <tr>
                  {th("name", "Training center")}
                  <th>Program</th>
                  {th("courses", "Courses", "r")}
                  {th("students", "Students", "r")}
                  {th("instructors", "Instructors", "r")}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="click" tabIndex={0} aria-label={`Open ${t.name}`} onClick={() => onOpen(t)} onKeyDown={(e) => e.key === "Enter" && onOpen(t)}>
                    <td>
                      <b style={{ fontWeight: 600 }}>{t.name}</b>
                      <div className="sub" style={{ fontSize: 12.5 }}><span className="mono">{t.code}</span>{t.region ? ` · ${t.region}` : ""}</div>
                    </td>
                    <td><Cat c={programStyle(t.program).label} /></td>
                    <td className="r mono">{fmtNumber(t.courses)}</td>
                    <td className="r mono">{fmtNumber(t.students)}</td>
                    <td className="r mono">{fmtNumber(t.instructors)}</td>
                    <td className="r"><span className="sub" aria-hidden="true"><I.chev /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!error && filtered.length > 0 && <div className="pfoot">Showing {filtered.length} of {tenants.length} training centers</div>}
      </Panel>
    </>
  );
}
